import React from 'react';
import {
  EdgeProps,
  EdgeLabelRenderer,
  BaseEdge,
  getStraightPath,
} from 'reactflow';
import { useUIStore } from '../../store';

/**
 * Custom edge component for execution flow connections
 * Displays as a thick white/off-white straight line with large arrow
 */
export const ExecutionFlowEdge: React.FC<EdgeProps> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  selected,
}) => {
  const theme = useUIStore((state) => state.theme);

  // Use straight path for execution flow
  const [edgePath, labelX, labelY] = getStraightPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
  });

  // Determine arrow color based on theme (not selection - selection adds glow via CSS)
  const arrowColor = theme === 'dark' ? '#e5e5e5' : '#64748b';

  // Style based on theme and selection state
  const edgeStyle = {
    ...style,
    stroke: arrowColor,
    strokeWidth: selected ? 6 : 4,
    strokeLinecap: 'round' as const,
    filter: selected ? 'drop-shadow(0 0 6px currentColor) brightness(1.3)' : 'none',
  };

  return (
    <>
      {/* Define marker in a hidden SVG */}
      <svg style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}>
        <defs>
          <marker
            id={`execution-arrow-${id}`}
            markerWidth="4"
            markerHeight="4"
            refX="3.5"
            refY="2"
            orient="auto"
            markerUnits="strokeWidth"
          >
            <polygon
              points="0,0 0,4 4,2"
              fill={arrowColor}
            />
          </marker>
        </defs>
      </svg>
      
      {/* Render the edge with custom arrow */}
      <BaseEdge
        id={id}
        path={edgePath}
        style={edgeStyle}
        markerEnd={`url(#execution-arrow-${id})`}
      />

      {/* Optional: Add label renderer for future enhancements */}
      <EdgeLabelRenderer>
        {selected && (
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: 'all',
              fontSize: '10px',
              fontWeight: 600,
              color: '#3b82f6',
              backgroundColor: theme === 'dark' ? '#1f2937' : '#ffffff',
              padding: '2px 6px',
              borderRadius: '4px',
              border: '1px solid #3b82f6',
            }}
          >
            Execution Flow
          </div>
        )}
      </EdgeLabelRenderer>
    </>
  );
};

